import { Root, Trigger } from '@radix-ui/react-dropdown-menu';
import { memo, useCallback } from 'react';
import { AccountSelectorMenu } from './AccountSelector.client';
import { AccountDisplayWithMembership } from '~/components/ui/AccountDisplay';
import * as Tooltip from '~/components/ui/Tooltip';
import { REFERRALS_FEATURE_ENABLED } from '~/lib/constants';
import { menuStore, SettingsPage } from '~/lib/stores/menu';
import { HELP_CENTER_URL } from '~/lib/urls';
import type { OrganizationData } from '~/types/organization';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';

const menuItemClassName =
  'flex items-center gap-2 p-2 rounded-lg bg-transparent hover:bg-bolt-elements-item-backgroundActive';

interface MenuItemsProps {
  user: UserData;
  isAccountSelectorMenuVisible: boolean;
  activeOrganization: OrganizationData | null;
  onToggleAccountSelector: VoidFunction;
  showAccountSelector: boolean;
}

export const MenuItems = memo(
  ({
    user,
    isAccountSelectorMenuVisible,
    onToggleAccountSelector,
    activeOrganization,
    showAccountSelector,
  }: MenuItemsProps) => {
    const disableReferrals = !REFERRALS_FEATURE_ENABLED;

    return (
      <>
        <div className="select-none">
          <div className="bg-bolt-elements-background-depth-1 border-t border-bolt-elements-borderColor overflow-hidden">
            <div className="flex flex-col p-2 space-y-2">
              {!activeOrganization && <RefferalsMenuItem disableReferrals={disableReferrals} />}

              <SettingsMenuItem />

              <a href={HELP_CENTER_URL} target="_blank" className={menuItemClassName}>
                <span className="i-ph:question text-lg" />
                Help Center
              </a>

              <hr className="h-px border-bolt-elements-borderColor" />

              {showAccountSelector && <MySubscriptionMenuItem />}

              {user && showAccountSelector && (
                <Root open={isAccountSelectorMenuVisible} onOpenChange={onToggleAccountSelector}>
                  <Trigger asChild>
                    <button className={menuItemClassName}>
                      <span className="i-ph:users text-lg" />
                      Select Account
                    </button>
                  </Trigger>
                  <AccountSelectorMenu user={user} activeOrganization={activeOrganization} />
                </Root>
              )}

              <LogoutMenuItem />
            </div>
          </div>

          <button
            className="flex items-center justify-start p-4 border-t border-bolt-elements-borderColor w-full bg-transparent focus-visible:outline-2 hover:bg-bolt-ds-inverseSurface/3 rounded-br-3xl"
            onClick={openSubscriptionSettings}
          >
            <AccountDisplayWithMembership
              type={activeOrganization ? 'organization' : 'user'}
              accountName={user?.name || user?.username}
              membershipLevel={activeOrganization ? activeOrganization.billing?.tier : user.membership?.tier}
              avatar={activeOrganization ? activeOrganization.avatar : user.avatar}
            />
          </button>
        </div>
      </>
    );
  },
);

interface RefferalsMenuItemProps {
  disableReferrals?: boolean;
}

const RefferalsMenuItem = memo(({ disableReferrals }: RefferalsMenuItemProps) => {
  const handleMenuItemClick = useCallback(() => {
    menuStore.openDialog({ type: 'referrals' });
  }, []);

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <button
            disabled={disableReferrals}
            className={classNames(menuItemClassName, 'text-bolt-elements-pricing-promo-text', {
              'cursor-not-allowed hover:bg-transparent': disableReferrals,
            })}
            onClick={handleMenuItemClick}
          >
            <span className="i-ph:gift text-lg" />
            Get free tokens
            <div className="flex-grow" />
            {disableReferrals && <span className="i-ph:lock text-lg" />}
          </button>
        </Tooltip.Trigger>
        {disableReferrals && (
          <Tooltip.Portal>
            <Tooltip.Content side="top" align="center" sideOffset={6} maxWidth={400}>
              We had to temporarily disable this feature, please come back later!
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    </Tooltip.Provider>
  );
});

const SettingsMenuItem = memo(() => {
  const handleMenuItemClick = useCallback(() => {
    menuStore.openDialog({ type: 'settings' });
  }, []);

  return (
    <button className={menuItemClassName} onClick={handleMenuItemClick}>
      <span className="i-ph:gear-six-duotone text-lg" />
      Settings
    </button>
  );
});

const MySubscriptionMenuItem = memo(() => {
  return (
    <button className={menuItemClassName} onClick={openSubscriptionSettings}>
      <span className="i-ph:credit-card-duotone text-lg" />
      My Subscription
    </button>
  );
});

const LogoutMenuItem = memo(() => {
  const handleMenuItemClick = useCallback(() => {
    confirmLogout();
  }, []);

  return (
    <button className={menuItemClassName} onClick={handleMenuItemClick}>
      <span className="i-ph:sign-out text-lg" />
      Sign Out
    </button>
  );
});

function confirmLogout() {
  menuStore.openDialog({ type: 'confirm-logout' });
}

function openSubscriptionSettings() {
  menuStore.openDialog({
    type: 'settings',
    initialPage: SettingsPage.SubscriptionAndTokens,
  });
}
